"use client"

import { useEffect, useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table"
import { Plus, Edit, Users, Search, Filter, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { Pagination } from "../../common/pagination-dynamic"
import { AddMappingModal } from "../../modals/add-mapping-modal"
import { useToast } from "../../hooks/use-toast"
import { Input } from "../../ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select"
import axios from "axios"

interface UserPolicyMapping {
  id: number
  employee: string
  policyName: string
  startDate: string
  endDate: string
  modifiedBy: string
  modifiedOn: string
}

type ApiUserPolicyMapping = {
  userID: number
  userName: string
  policyID: number
  policyName: string
  startDate: string
  endDate: string
  modifiedBy: string
  modifiedOn: string
}

type ApiUser = {
  userID: number
  userName: string
}

type ApiPolicyRow = {
  policyID: number
  policyName: string
}

export function UserPolicyMappingPage() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingMapping, setEditingMapping] = useState<UserPolicyMapping | null>(null)
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [userPolicyMappings, setUserPolicyMappings] = useState<UserPolicyMapping[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [userNameToId, setUserNameToId] = useState<Map<string, number>>(new Map())
  const [policyNameToId, setPolicyNameToId] = useState<Map<string, number>>(new Map())
  const [availableUsers, setAvailableUsers] = useState<{ id: number, name: string }[]>([])
  const [availablePolicies, setAvailablePolicies] = useState<{ id: number, name: string }[]>([])
  const { toast } = useToast()

  // Search, Filter, and Sort states
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPolicy, setFilterPolicy] = useState<string>('all')
  const [sortField, setSortField] = useState<keyof UserPolicyMapping | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      ////console.log("[UserPolicyMapping] Fetching mappings, users, and policies...")
      const [mappingsResp, usersResp, policiesResp] = await Promise.all([
        axios.get<ApiUserPolicyMapping[]>(process.env.REACT_APP_API_URL +"/api/Leave/GetUserPolicyMapping", { withCredentials: true }),
        axios.get<ApiUser[]>(process.env.REACT_APP_API_URL +"/api/Leave/GetAllUsersWithoutPolicy", { withCredentials: true }),
        axios.get<ApiPolicyRow[]>(process.env.REACT_APP_API_URL +"/api/Leave/GetAllActiveLeavePolicies", { withCredentials: true }),
      ])

      ////console.log("[UserPolicyMapping] Raw mappings:", mappingsResp.data)
      ////console.log("[UserPolicyMapping] Raw users:", usersResp.data)
      ////console.log("[UserPolicyMapping] Raw policies:", policiesResp.data)

      const mappings: UserPolicyMapping[] = (mappingsResp.data || []).map((m) => ({
        id: m.userID,
        employee: m.userName,
        policyName: m.policyName,
        startDate: m.startDate?.split("T")[0] || "",
        endDate: m.endDate?.split("T")[0] || "",
        modifiedBy: m.modifiedBy,
        modifiedOn: new Date(m.modifiedOn).toLocaleString(),
      }))
      setUserPolicyMappings(mappings)

      // Build user name -> id map from HRAdmin endpoint, try common field names
      const uMap = new Map<string, number>()
      const usersArr: { id: number, name: string }[] = []
      for (const u of usersResp.data || []) {
        const name = (u.userName || "").toString()
        const id = Number(u.userID)
        if (name && !Number.isNaN(id)) {
          uMap.set(name, id)
          usersArr.push({ id, name })
        }
      }
      setUserNameToId(uMap)
      setAvailableUsers(usersArr)

      // Build policy name -> id map from policy rows
      const pMap = new Map<string, number>()
      const policiesArr: { id: number, name: string }[] = []
      for (const p of policiesResp.data || []) {
        if (p.policyName && p.policyID) pMap.set(p.policyName, p.policyID)
        if (p.policyName && p.policyID) policiesArr.push({ id: p.policyID, name: p.policyName })
      }
      setPolicyNameToId(pMap)
      setAvailablePolicies(policiesArr)
      ////console.log("[UserPolicyMapping] Built name->id maps", { users: Array.from(uMap.entries()), policies: Array.from(pMap.entries()) })
    } catch (e: any) {
      console.error("[UserPolicyMapping] Load failed:", e)
      setError(e?.message || "Failed to load data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Reset to first page when filters/search change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterPolicy, sortField, sortDirection])

  // Sorting function
  const handleSort = (field: keyof UserPolicyMapping) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Get sort icon
  const getSortIcon = (field: keyof UserPolicyMapping) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4" /> : 
      <ArrowDown className="h-4 w-4" />
  }

  // Get unique policies for filter dropdown
  const uniquePolicies = useMemo(() => {
    const policies = new Set<string>()
    userPolicyMappings.forEach(mapping => {
      if (mapping.policyName) policies.add(mapping.policyName)
    })
    return Array.from(policies)
  }, [userPolicyMappings])

  // Filter and sort data
  const filteredAndSortedMappings = useMemo(() => {
    let filtered = userPolicyMappings.filter(mapping => {
      const matchesSearch = 
        mapping.employee.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mapping.policyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mapping.modifiedBy.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesPolicy = filterPolicy === 'all' || mapping.policyName === filterPolicy
      
      return matchesSearch && matchesPolicy
    })

    if (sortField) {
      filtered.sort((a, b) => {
        let aValue: any = a[sortField]
        let bValue: any = b[sortField]
        
        // Handle date fields
        if (sortField === 'startDate' || sortField === 'endDate' || sortField === 'modifiedOn') {
          aValue = new Date(aValue).getTime()
          bValue = new Date(bValue).getTime()
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase()
          bValue = bValue.toLowerCase()
        }
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
        }
        
        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
        }
      })
    }

    return filtered
  }, [userPolicyMappings, searchTerm, filterPolicy, sortField, sortDirection])

  const handleEdit = (mapping: UserPolicyMapping) => {
    setEditingMapping(mapping)
    setIsAddModalOpen(true)
  }

  const handleSave = async (data: any) => {
    try {
      // Resolve names from form or fall back to the editing record values
      const selectedUserName: string | undefined = (data.employee || editingMapping?.employee)
      const selectedPolicyName: string | undefined = (data.policyName || editingMapping?.policyName)

      // Map display names to IDs with fallbacks
      const userID = (selectedUserName ? userNameToId.get(selectedUserName) : undefined) ?? editingMapping?.id
      const policyID = selectedPolicyName ? policyNameToId.get(selectedPolicyName) : undefined

      if (!userID || !policyID) {
        toast({
          title: "Missing IDs",
          description: "Could not resolve User or Policy ID. Please ensure valid selections.",
          duration: 4000,
          className: "border border-red-300",
        })
        return
      }

      const payload = {
        userID,
        policyID,
        startDate: data.startDate ? new Date(data.startDate).toISOString() : (editingMapping?.startDate ? new Date(editingMapping.startDate).toISOString() : new Date().toISOString()),
        endDate: data.endDate ? new Date(data.endDate).toISOString() : (editingMapping?.endDate ? new Date(editingMapping.endDate).toISOString() : new Date().toISOString()),
        modUser: 0,
      }
      // Validate date ordering before calling API
      if (new Date(payload.startDate) > new Date(payload.endDate)) {
        toast({
          title: "Invalid Dates",
          description: "Start date must be earlier than end date.",
          duration: 4000,
          className: "border border-red-300",
        })
        return
      }
      ////console.log("[UserPolicyMapping] POST payload:", payload)

      await axios.post(process.env.REACT_APP_API_URL +"/api/Leave/SaveUserPolicyMapping", payload, { withCredentials: true })

      toast({
        title: "Success",
        description: editingMapping ? `Mapping for "${selectedUserName}" updated successfully!` : `Mapping for "${selectedUserName}" added successfully!`,
        duration: 3000,
        className: "border border-green-300",
      })

      await loadData()
    } catch (e: any) {
      console.error("[UserPolicyMapping] Save failed:", e)
      toast({
        title: "Error",
        description: e?.message || "Failed to save mapping",
        duration: 4000,
        className: "border border-red-300",
      })
    } finally {
      setIsAddModalOpen(false)
      setEditingMapping(null)
    }
  }

  const totalPages = Math.ceil(filteredAndSortedMappings.length / pageSize)
  
  // Auto-reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [currentPage, totalPages])
  
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const currentData = filteredAndSortedMappings.slice(startIndex, endIndex)

  return (
    <div className="p-6">
      <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardHeader className="border-b border-gray-200 bg-white p-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-6 w-6 text-blue-600" />
                User Policy Mapping
              </CardTitle>
              <Button onClick={() => { setEditingMapping(null); setIsAddModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add New Mapping
              </Button>
            </div>

            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by employee, policy, or modifier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <Select value={filterPolicy} onValueChange={setFilterPolicy}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Policy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Policies</SelectItem>
                    {uniquePolicies.map((policy) => (
                      <SelectItem key={policy} value={policy}>
                        {policy}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {(searchTerm || filterPolicy !== 'all') && (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSearchTerm('')
                    setFilterPolicy('all')
                  }}
                  className="whitespace-nowrap"
                >
                  Clear Filters
                </Button>
              )}
            </div>
            
            {/* Results Summary */}
            <div className="text-sm text-gray-600">
              Showing {currentData.length} of {filteredAndSortedMappings.length} mappings
              {(searchTerm || filterPolicy !== 'all') && ` (filtered from ${userPolicyMappings.length} total)`}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
        {loading && (
          <div className="p-4 text-sm text-gray-600">Loading mappings...</div>
        )}
        {error && (
          <div className="p-4 text-sm text-red-600">{error}</div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 border-b border-gray-200">
              <TableHead className="font-semibold text-gray-700 p-4">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                  onClick={() => handleSort('employee')}
                >
                  Employee
                  {getSortIcon('employee')}
                </Button>
              </TableHead>
              <TableHead className="font-semibold text-gray-700 p-4">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                  onClick={() => handleSort('policyName')}
                >
                  Policy Name
                  {getSortIcon('policyName')}
                </Button>
              </TableHead>
              <TableHead className="font-semibold text-gray-700 p-4">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                  onClick={() => handleSort('startDate')}
                >
                  Start Date
                  {getSortIcon('startDate')}
                </Button>
              </TableHead>
              <TableHead className="font-semibold text-gray-700 p-4">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                  onClick={() => handleSort('endDate')}
                >
                  End Date
                  {getSortIcon('endDate')}
                </Button>
              </TableHead>
              <TableHead className="font-semibold text-gray-700 p-4">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                  onClick={() => handleSort('modifiedBy')}
                >
                  Modified By
                  {getSortIcon('modifiedBy')}
                </Button>
              </TableHead>
              <TableHead className="font-semibold text-gray-700 p-4">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                  onClick={() => handleSort('modifiedOn')}
                >
                  Modified On
                  {getSortIcon('modifiedOn')}
                </Button>
              </TableHead>
              <TableHead className="w-20 font-semibold text-gray-700 p-4">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentData.map((mapping) => (
              <TableRow key={mapping.id} className="hover:bg-gray-50 border-b border-gray-100">
                <TableCell className="font-medium text-gray-900 p-4">{mapping.employee}</TableCell>
                <TableCell className="text-gray-700 p-4">{mapping.policyName}</TableCell>
                <TableCell className="text-gray-700 p-4">{mapping.startDate}</TableCell>
                <TableCell className="text-gray-700 p-4">{mapping.endDate}</TableCell>
                <TableCell className="text-gray-700 p-4">{mapping.modifiedBy}</TableCell>
                <TableCell className="text-gray-700 p-4">{mapping.modifiedOn}</TableCell>
                <TableCell className="p-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(mapping)}
                    className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={filteredAndSortedMappings.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
        />
        </CardContent>
      </Card>

      <AddMappingModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSave}
        initialData={editingMapping}
        users={availableUsers}
        policies={availablePolicies}
      />
    </div>
  )
}
